import { useState } from "react";
import Table from "rsuite/Table";
import Pagination from "rsuite/Pagination";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";

import { useRootDispatch } from "store/hooks";
import { useRequest } from "hooks/useRequest";

import { typeMapper } from "utility";
import { PAGE_COUNT } from "constants/common";

import classes from "./FtTable.module.scss";

export interface FtTableHeadData {
  id: string;
  label: string;
  width?: number;
  cellClassName?: string;
  alwaysShow?: boolean;
  sortable?: boolean;
  type?: "SHARE-DOWMLOAD" | "ACTION";
  align?: "right" | "left" | "center";
  dynamicRender?: ({
    value,
    data,
    t,
  }: {
    value?: any;
    data: any;
    t: TFunction;
  }) => any;
}

export interface FtTableProps<T> {
  loading?: boolean;
  hover?: boolean;
  fillHeight?: boolean;
  autoHeight?: boolean;
  showHeader?: boolean;
  bordered?: boolean;
  cellBordered?: boolean;
  compact?: boolean;
  sortColumn?: string;
  sortDir?: any;
  sortMap?: string;
  pageNum?: number;
  expandedView?: boolean;
  headData: FtTableHeadData[];
  tableData: any;
  pagination?: boolean;
  totalCount?: number;
  dataAction: any;
  filters: T;
  createFilterProps: any;
  rowClickhandler?: any;
  paginationAction?: any;
  pageCount?: number;
  tableHeight?: string;
  rowClassName?: string | any;
  rowExpandedHeight?: number;
  expandColumnWidth?: number;
  shortPagination?: boolean;
  emptyMessage?: string;
}

const rowKey = "id";

export type SortType = "desc" | "asc";

const ExpandCell = ({
  rowData,
  dataKey,
  expandedRowKeys,
  onChange,
  ...props
}: any) => (
  <Table.Cell {...props}>
    <ft-icon
      class={classes.ExpandedIcon}
      onClick={(e: Event) => {
        e.stopPropagation();
        e.preventDefault();
        onChange(rowData, dataKey);
      }}
    >
      {expandedRowKeys.some((key: any) => key === rowData[rowKey])
        ? "arrow_drop_down"
        : "arrow_right"}
    </ft-icon>
  </Table.Cell>
);

const CompactHeaderCell = (props: any) => (
  <Table.HeaderCell
    {...props}
    style={{ fontWeight: "500", fontSize: "14px" }}
    className="gray-background"
  />
);

export const FtTable = <T,>({
  loading = false,
  hover = true,
  fillHeight = true,
  autoHeight = false,
  showHeader = true,
  bordered = false,
  cellBordered = false,
  compact = false,
  sortColumn,
  sortDir,
  sortMap = "SortColumn",
  pageNum = 1,
  expandedView = true,
  headData,
  tableData,
  pagination = false,
  totalCount,
  dataAction,
  createFilterProps,
  rowClickhandler,
  paginationAction,
  pageCount = PAGE_COUNT,
  tableHeight,
  emptyMessage,
  rowClassName,
  rowExpandedHeight,
  expandColumnWidth = 70,
  shortPagination,
  filters,
}: FtTableProps<T>) => {
  const [shouldUpdateScroll, setShouldUpdateScroll] = useState(true);
  const [expandedRowKeys, setExpandedRowKeys] = useState<any>([]);
  const dispatch = useRootDispatch();
  const { request } = useRequest();
  const { t } = useTranslation(["common"]);

  const handleExpanded = (rowData: any, dataKey: string) => {
    let open = false;
    const nextExpandedRowKeys = [];
    setShouldUpdateScroll(false);
    setTimeout(() => {
      setShouldUpdateScroll(true);
    }, 0);

    expandedRowKeys.forEach((key: number) => {
      if (key === rowData[rowKey]) {
        open = true;
      } else {
        nextExpandedRowKeys.push(key);
      }
    });

    if (!open) {
      nextExpandedRowKeys.push(rowData[rowKey]);
    }

    setExpandedRowKeys(nextExpandedRowKeys);
    /* 
    const nextExpandedRowKeys: any = [rowData[dataKey]];
    setExpandedRowKeys(nextExpandedRowKeys); */
  };

  const sortHandler = (sortColumn: string, sortType?: SortType | undefined) => {
    const filterDTO = createFilterProps(filters);
    dispatch(
      dataAction({
        request,
        data: {
          sortColumn: typeMapper(sortColumn, sortMap),
          sortDir: typeMapper(sortType as string, "SortDir"),
          ...filterDTO,
        },
      }),
    );
  };

  const setPage = (page: number) => {
    if (paginationAction) {
      paginationAction(page);
    } else {
      const filterDTO = createFilterProps(filters);
      dispatch(
        dataAction({
          request,
          data: {
            pageNumber: page,
            sortColumn,
            sortDir,
            ...filterDTO,
          },
        }),
      );
    }
  };

  const renderRowExpanded = (rowData: any) => {
    const renderItems = headData
      .filter((single: FtTableHeadData) => !single.alwaysShow && !single.type)
      .map((single: FtTableHeadData, i) => {
        return (
          <div key={i} className={classes.ExpansionDetail}>
            <div className={classes.TableExpansion}>
              <p>{single.label}</p>
            </div>
            <div>
              <p className={classes.TableExpansionValues}>
                {single.dynamicRender
                  ? single.dynamicRender({
                      value: rowData[single.id],
                      data: rowData,
                      t,
                    })
                  : rowData[single.id]}
              </p>
            </div>
          </div>
        );
      });

    const actionsItems = headData
      .filter((single: FtTableHeadData) => single.type)
      .map((single: FtTableHeadData, i) => {
        return (
          <div key={i} className={classes.ExpansionActions}>
            {single.dynamicRender
              ? single.dynamicRender({
                  value: rowData[single.id],
                  data: rowData,
                  t,
                })
              : rowData[single.id]}
          </div>
        );
      });

    return (
      <div className={classes.ExpansionContainer}>
        {renderItems}
        <div className={classes.ExpansionActionWarpper}>{actionsItems}</div>
      </div>
    );
  };

  const renderMobileColumn = (data: FtTableHeadData[]) =>
    data
      .filter((single: FtTableHeadData) => single.alwaysShow)
      .map((single: FtTableHeadData, index: number) => (
        <Table.Column
          key={index}
          flexGrow={single.width == null ? 1 : undefined}
          sortable={single.sortable}
          minWidth={single.width}
          width={single.width}
        >
          <CompactHeaderCell>{single.label}</CompactHeaderCell>
          <RenderCell dataHead={single} />
        </Table.Column>
      ));

  const renderColumn = (data: FtTableHeadData[]) =>
    data.map((single: FtTableHeadData, index: number) => {
      return (
        <Table.Column
          key={index}
          flexGrow={single.width == null ? 1 : undefined}
          sortable={single.sortable}
          align={single.align}
          minWidth={single.width}
          width={single.width}
        >
          <CompactHeaderCell>{single.label}</CompactHeaderCell>
          <RenderCell dataHead={single} dataKey={single.id} />
        </Table.Column>
      );
    });

  const RenderCell = ({ rowData, dataHead, ...props }: any) => {
    const head: FtTableHeadData = dataHead;

    return (
      <Table.Cell {...props} className={head.cellClassName}>
        {head.dynamicRender
          ? head.dynamicRender({
              value: rowData[head.id],
              data: rowData,
              t,
            })
          : rowData[head.id]}
      </Table.Cell>
    );
  };

  return (
    <>
      <div style={tableHeight ? { height: tableHeight } : {}}>
        <Table
          shouldUpdateScroll={shouldUpdateScroll}
          className={[classes.Table, "rsuite-custom"].join(" ")}
          loading={loading}
          hover={hover}
          fillHeight={fillHeight}
          showHeader={showHeader}
          autoHeight={autoHeight}
          bordered={bordered}
          cellBordered={cellBordered}
          sortColumn={sortColumn}
          sortType={sortDir}
          headerHeight={compact ? 30 : 40}
          rowHeight={compact ? 30 : 46}
          data={tableData}
          rowKey={rowKey}
          expandedRowKeys={expandedRowKeys}
          onRowClick={(data: any) => {
            rowClickhandler && rowClickhandler(data);
          }}
          onSortColumn={sortHandler}
          renderRowExpanded={expandedView ? renderRowExpanded : undefined}
          rowExpandedHeight={rowExpandedHeight || 160}
          rowClassName={rowClassName}
          locale={{
            emptyMessage: emptyMessage || t("common:no_data_available"),
          }}
        >
          {expandedView
            ? [
                renderMobileColumn(headData),
                <Table.Column
                  width={expandColumnWidth}
                  key="temp"
                  align="center"
                >
                  <CompactHeaderCell>{""}</CompactHeaderCell>
                  <ExpandCell
                    dataKey="id"
                    expandedRowKeys={expandedRowKeys}
                    onChange={handleExpanded}
                  />
                </Table.Column>,
              ]
            : renderColumn(headData)}
        </Table>
      </div>
      {pagination ? (
        <div className={[classes.Pagination, "rsuite-custom"].join(" ")}>
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            limit={pageCount}
            size="xs"
            layout={
              totalCount
                ? shortPagination
                  ? ["-", "-", "pager"]
                  : ["total", "-", "pager"]
                : []
            }
            total={totalCount ? totalCount : 0}
            activePage={pageNum}
            onChangePage={setPage}
            maxButtons={2}
          />
        </div>
      ) : null}
    </>
  );
};
